import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FolderSearch, RefreshCw, FileText, FileCheck, Banknote, Shield, 
  Building2, Heart, Mail, Wrench, ExternalLink, CheckCircle2, Filter 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  facture: { icon: Banknote, color: 'bg-green-100 text-green-700', label: 'Facture' },
  contrat: { icon: FileCheck, color: 'bg-blue-100 text-blue-700', label: 'Contrat' },
  msa: { icon: Building2, color: 'bg-purple-100 text-purple-700', label: 'MSA / Social' },
  reglementaire: { icon: Shield, color: 'bg-red-100 text-red-700', label: 'Reglementaire' },
  bancaire: { icon: Banknote, color: 'bg-amber-100 text-amber-700', label: 'Bancaire' },
  assurance: { icon: Heart, color: 'bg-pink-100 text-pink-700', label: 'Assurance' },
  courrier: { icon: Mail, color: 'bg-gray-100 text-gray-700', label: 'Courrier' },
  technique: { icon: Wrench, color: 'bg-cyan-100 text-cyan-700', label: 'Technique' },
};

const DriveMonitorPage = () => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    if (societeId) headers['X-Societe-Id'] = societeId;
    return headers;
  }, []);

  const fetchDocuments = useCallback(async (cat = null) => {
    setLoading(true);
    try {
      const url = cat 
        ? `${API_URL}/api/drive-monitor/documents?categorie=${cat}`
        : `${API_URL}/api/drive-monitor/documents`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [getHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/drive-monitor/stats`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) { /* silent */ }
  }, [getHeaders]);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [fetchDocuments, fetchStats]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/drive-monitor/scan`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ since_hours: 24 })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.files_processed || 0} fichiers analyses`);
        await fetchDocuments();
        await fetchStats();
      }
    } catch (e) {
      toast.error('Erreur lors du scan');
    }
    setScanning(false);
  };

  const handleValidate = async (docId, newCat = null) => {
    try {
      const body = newCat ? { categorie: newCat } : {};
      const res = await fetch(`${API_URL}/api/drive-monitor/documents/${docId}/validate`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success('Classification validee');
        await fetchDocuments(activeFilter);
      }
    } catch (e) {
      toast.error('Erreur validation');
    }
  };

  const handleFilter = (cat) => {
    setActiveFilter(cat === activeFilter ? null : cat);
    fetchDocuments(cat === activeFilter ? null : cat);
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6" data-testid="drive-monitor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FolderSearch className="w-7 h-7 text-blue-600" />
            Monitoring Google Drive
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tri automatique de vos documents par l'IA
          </p>
        </div>
        <Button 
          onClick={handleScan} 
          disabled={scanning}
          className="flex items-center gap-2"
          data-testid="scan-drive-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scan en cours...' : 'Scanner le Drive'}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="drive-stats">
          <Card className="text-center p-3">
            <p className="text-2xl font-bold text-blue-600">{stats.total_documents}</p>
            <p className="text-xs text-gray-500">Total documents</p>
          </Card>
          <Card className="text-center p-3">
            <p className="text-2xl font-bold text-green-600">{stats.total_validated}</p>
            <p className="text-xs text-gray-500">Valides</p>
          </Card>
          <Card className="text-center p-3">
            <p className="text-2xl font-bold text-amber-600">{stats.total_documents - stats.total_validated}</p>
            <p className="text-xs text-gray-500">A valider</p>
          </Card>
          <Card className="text-center p-3">
            <p className="text-2xl font-bold text-gray-600">{Object.keys(stats.categories || {}).length}</p>
            <p className="text-xs text-gray-500">Categories</p>
          </Card>
        </div>
      )}

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2" data-testid="category-filters">
        {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button
              key={key}
              onClick={() => handleFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                activeFilter === key ? 'ring-2 ring-offset-1 ring-blue-400 ' + cfg.color : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {cfg.label}
            </button>
          );
        })}
      </div>

      {/* Documents List */}
      <div className="space-y-3" data-testid="documents-list">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Chargement...</div>
        ) : documents.length === 0 ? (
          <Card className="text-center py-12">
            <FolderSearch className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Aucun document classe</p>
            <p className="text-sm text-gray-400 mt-1">Lancez un scan du Drive pour commencer</p>
          </Card>
        ) : (
          documents.map((doc) => {
            const catCfg = CATEGORY_CONFIG[doc.categorie] || CATEGORY_CONFIG.courrier;
            const CatIcon = catCfg.icon;
            return (
              <Card key={doc.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`p-2.5 rounded-lg ${catCfg.color}`}>
                    <CatIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{doc.drive_file_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {catCfg.label} • Confiance: {Math.round((doc.confiance || 0) * 100)}%
                      {doc.montant && ` • ${doc.montant} EUR`}
                      {doc.emetteur && ` • ${doc.emetteur}`}
                    </p>
                    {doc.resume && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{doc.resume}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {doc.valide_par_utilisateur ? (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle2 className="w-4 h-4" /> Valide
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleValidate(doc.id)}
                        className="text-xs"
                      >
                        Valider
                      </Button>
                    )}
                    {doc.drive_web_link && (
                      <a
                        href={doc.drive_web_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DriveMonitorPage;
